/**
 * Store Form Component
 * 
 * Beautiful form component for creating and editing stores.
 * Maintains design consistency with the rest of the application.
 * 
 * @module StoreForm
 */

import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import {
  Store as StoreIcon,
  MapPin,
  Phone,
  Mail,
  Calendar,
  User,
  FileText,
  X,
  Save,
  Building2
} from 'lucide-react';
import { Store } from '../../@types/store';
import { ThemeColors, Spacing, Typography, BorderRadius, Shadows } from '../../styles/ThemeStyles';

/**
 * StoreForm props interface
 */
export interface StoreFormProps {
  /** Store to edit (undefined for new store) */
  store?: Store;
  
  /** Form mode */
  mode: 'create' | 'edit';
  
  /** Loading state */
  isLoading?: boolean;
  
  /** Handle form submission */
  onSubmit: (data: Partial<Store>) => Promise<void>;
  
  /** Handle form cancellation */
  onCancel: () => void;
  
  /** Available sales reps for assignment */
  salesReps?: Array<{ id: string; name: string; username?: string; salesGroup?: string }>;
}

/**
 * Styled components
 */
const FormOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: ${Spacing.lg};
`;

const FormContainer = styled.div`
  background: white;
  border-radius: ${BorderRadius.xl};
  box-shadow: ${Shadows.xl};
  width: 100%;
  max-width: 700px;
  max-height: 90vh;
  overflow-y: auto;
`;

const FormHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${Spacing.lg};
  border-bottom: 1px solid ${ThemeColors.border.light};
  position: sticky;
  top: 0;
  background: white;
  z-index: 1;
`;

const FormTitle = styled.h2`
  font-size: ${Typography.fontSize.xl};
  font-weight: ${Typography.fontWeight.bold};
  color: ${ThemeColors.text.primary};
  margin: 0;
  display: flex;
  align-items: center;
  gap: ${Spacing.sm};
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  padding: ${Spacing.sm};
  cursor: pointer;
  color: ${ThemeColors.text.secondary};
  border-radius: ${BorderRadius.sm};
  transition: all 0.2s ease;

  &:hover {
    background-color: ${ThemeColors.background.secondary};
    color: ${ThemeColors.text.primary};
  }

  svg {
    width: 20px;
    height: 20px;
  }
`;

const FormContent = styled.div`
  padding: ${Spacing.lg};
`;

const FormGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${Spacing.lg};
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const FormSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${Spacing.md};
`;

const SectionTitle = styled.h3`
  font-size: ${Typography.fontSize.lg};
  font-weight: ${Typography.fontWeight.semibold};
  color: ${ThemeColors.text.primary};
  margin: 0;
  display: flex;
  align-items: center;
  gap: ${Spacing.sm};
  padding-top: ${Spacing.md};
  border-top: 1px solid ${ThemeColors.border.light};
  
  &:first-child {
    padding-top: 0;
    border-top: none;
  }

  svg {
    width: 20px;
    height: 20px;
    color: ${ThemeColors.primary.main};
  }
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${Spacing.xs};
`;

const Label = styled.label`
  font-size: ${Typography.fontSize.sm};
  font-weight: ${Typography.fontWeight.medium};
  color: ${ThemeColors.text.primary};
  display: flex;
  align-items: center;
  gap: ${Spacing.xs};
`;

const Required = styled.span`
  color: ${ThemeColors.error.main};
`;

const Input = styled.input`
  padding: ${Spacing.sm} ${Spacing.md};
  border: 1px solid ${ThemeColors.border.main};
  border-radius: ${BorderRadius.sm};
  font-size: ${Typography.fontSize.sm};
  transition: all 0.2s ease;

  &:focus {
    outline: none;
    border-color: ${ThemeColors.primary.main};
    box-shadow: 0 0 0 3px ${ThemeColors.primary.light};
  }

  &:disabled {
    background-color: ${ThemeColors.background.secondary};
    color: ${ThemeColors.text.secondary};
    cursor: not-allowed;
  }

  &::placeholder {
    color: ${ThemeColors.text.secondary};
  }
`;

const Select = styled.select`
  padding: ${Spacing.sm} ${Spacing.md};
  border: 1px solid ${ThemeColors.border.main};
  border-radius: ${BorderRadius.sm};
  font-size: ${Typography.fontSize.sm};
  background-color: white;
  cursor: pointer;
  transition: all 0.2s ease;

  &:focus {
    outline: none;
    border-color: ${ThemeColors.primary.main};
    box-shadow: 0 0 0 3px ${ThemeColors.primary.light};
  }

  &:disabled {
    background-color: ${ThemeColors.background.secondary};
    color: ${ThemeColors.text.secondary};
    cursor: not-allowed;
  }
`;

const TextArea = styled.textarea`
  padding: ${Spacing.sm} ${Spacing.md};
  border: 1px solid ${ThemeColors.border.main};
  border-radius: ${BorderRadius.sm};
  font-size: ${Typography.fontSize.sm};
  font-family: inherit;
  resize: vertical;
  min-height: 80px;
  transition: all 0.2s ease;

  &:focus {
    outline: none;
    border-color: ${ThemeColors.primary.main};
    box-shadow: 0 0 0 3px ${ThemeColors.primary.light};
  }

  &::placeholder {
    color: ${ThemeColors.text.secondary};
  }
`;

const FormActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: ${Spacing.md};
  padding: ${Spacing.lg};
  border-top: 1px solid ${ThemeColors.border.light};
  margin-top: ${Spacing.lg};
  position: sticky;
  bottom: 0;
  background: white;
`;

const Button = styled.button<{ variant?: 'primary' | 'secondary' }>`
  display: flex;
  align-items: center;
  gap: ${Spacing.sm};
  padding: ${Spacing.sm} ${Spacing.lg};
  border: none;
  border-radius: ${BorderRadius.md};
  font-size: ${Typography.fontSize.sm};
  font-weight: ${Typography.fontWeight.medium};
  cursor: pointer;
  transition: all 0.2s ease;

  ${props => props.variant === 'primary' ? `
    background: ${ThemeColors.primary.main};
    color: white;
    
    &:hover:not(:disabled) {
      background: ${ThemeColors.primary.dark};
    }
    
    &:disabled {
      background: ${ThemeColors.gray[400]};
      cursor: not-allowed;
    }
  ` : `
    background: white;
    color: ${ThemeColors.text.primary};
    border: 1px solid ${ThemeColors.border.main};
    
    &:hover:not(:disabled) {
      background: ${ThemeColors.background.secondary};
      border-color: ${ThemeColors.primary.main};
    }
  `}

  svg {
    width: 16px;
    height: 16px;
  }
`;

const ErrorMessage = styled.div`
  color: ${ThemeColors.error.main};
  font-size: ${Typography.fontSize.xs};
  margin-top: ${Spacing.xs};
`;

const FullWidthSection = styled.div`
  grid-column: 1 / -1;
`;

/**
 * StoreForm Component
 * 
 * @example
 * ```tsx
 * <StoreForm
 *   mode="create"
 *   isLoading={isCreating}
 *   onSubmit={handleCreateStore}
 *   onCancel={handleCancel}
 *   salesReps={salesReps}
 * />
 * ```
 */
export const StoreForm: React.FC<StoreFormProps> = ({
  store,
  mode,
  isLoading = false,
  onSubmit,
  onCancel,
  salesReps = []
}) => {
  const { t } = useTranslation();
  
  // Form state
  const [formData, setFormData] = useState<Partial<Store>>({
    name: '',
    address: '',
    city: '',
    state: '',
    zipCode: '',
    telephone: '',
    email: '',
    storeNumber: '',
    assignedSalesRep: '',
    status: 'active',
    notes: ''
  });
  
  // Multiple sales rep assignments state
  const [assignedUserIds, setAssignedUserIds] = useState<string[]>([]);

  const [errors, setErrors] = useState<Record<string, string>>({});

  // Initialize form data
  useEffect(() => {
    if (store && mode === 'edit') {
      setFormData({
        name: store.name || '',
        address: store.address || '',
        city: store.city || '',
        state: store.state || '',
        zipCode: store.zipCode || '',
        telephone: store.telephone || '',
        email: store.email || '',
        storeNumber: store.storeNumber || '',
        assignedSalesRep: store.assignedSalesRep || '',
        status: store.status || 'active',
        notes: store.notes || ''
      });
      
      // Initialize assigned user IDs - prefer existing assignments, otherwise auto-match by SalesGroup
      if (store.assignedToUserIds && Array.isArray(store.assignedToUserIds) && store.assignedToUserIds.length > 0) {
        // Use existing assignments
        setAssignedUserIds(store.assignedToUserIds);
      } else if (store.assignedSalesRep) {
        // Legacy: if old field exists, convert to array
        setAssignedUserIds([store.assignedSalesRep as string]);
      } else {
        // Auto-match users by SalesGroup if store has a SalesGroup
        // Check both SalesGroup (capital S - from Firestore) and salesGroup (lowercase - from type)
        const storeSalesGroup = ((store as any).SalesGroup || store.salesGroup || '').toUpperCase();
        if (storeSalesGroup && salesReps.length > 0) {
          const matchingUserIds = salesReps
            .filter(rep => {
              const repSalesGroup = (rep.salesGroup || '').toUpperCase();
              return repSalesGroup === storeSalesGroup;
            })
            .map(rep => rep.id);
          setAssignedUserIds(matchingUserIds);
        } else {
          setAssignedUserIds([]);
        }
      }
    }
  }, [store, mode, salesReps]);

  /**
   * Handle input changes
   */
  const handleInputChange = (field: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  /**
   * Validate form data
   */
  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.name?.trim()) {
      newErrors.name = 'Store name is required';
    }

    if (formData.email && !/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    if (formData.telephone && !/^\+?[\d\s\-\(\)]+$/.test(formData.telephone)) {
      newErrors.telephone = 'Please enter a valid phone number';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  /**
   * Handle form submission
   */
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      console.error('Form validation failed');
      return;
    }

    try {
      // Include assigned user IDs in the submission data
      const submissionData = {
        ...formData,
        assignedToUserIds: mode === 'edit' ? assignedUserIds : undefined
      };
      
      console.log('Submitting store data:', {
        ...submissionData,
        assignedToUserIds: submissionData.assignedToUserIds,
        assignedUserIdsCount: assignedUserIds.length
      });
      
      await onSubmit(submissionData);
    } catch (error) {
      console.error('Form submission error:', error);
      // Show error to user
      setErrors({ _form: error instanceof Error ? error.message : 'Failed to submit form' });
    }
  };
  
  /**
   * Toggle sales rep assignment
   */
  const handleSalesRepToggle = (userId: string) => {
    setAssignedUserIds(prev => {
      const newIds = prev.includes(userId)
        ? prev.filter(id => id !== userId)
        : [...prev, userId];
      
      console.log('Sales rep assignment toggled:', {
        userId,
        action: prev.includes(userId) ? 'removed' : 'added',
        previousCount: prev.length,
        newCount: newIds.length,
        newIds
      });
      
      return newIds;
    });
  };

  /**
   * Handle overlay click
   */
  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onCancel();
    }
  };

  return (
    <FormOverlay onClick={handleOverlayClick}>
      <FormContainer>
        <FormHeader>
          <FormTitle>
            {mode === 'create' ? (
              <>
                <Building2 />
                Create New Store
              </>
            ) : (
              <>
                <Building2 />
                Edit Store
              </>
            )}
          </FormTitle>
          <CloseButton onClick={onCancel}>
            <X />
          </CloseButton>
        </FormHeader>

        <form onSubmit={handleSubmit}>
          <FormContent>
            {/* Basic Information */}
            <SectionTitle>
              <StoreIcon />
              Basic Information
            </SectionTitle>
            
            <FormGrid>
              <FormGroup style={{ gridColumn: '1 / -1' }}>
                <Label htmlFor="name">
                  Store Name <Required>*</Required>
                </Label>
                <Input
                  id="name"
                  type="text"
                  value={formData.name}
                  onChange={(e) => handleInputChange('name', e.target.value)}
                  placeholder="Enter store name"
                  disabled={isLoading}
                />
                {errors.name && <ErrorMessage>{errors.name}</ErrorMessage>}
              </FormGroup>

              <FormGroup>
                <Label htmlFor="storeNumber">
                  Store Number
                </Label>
                <Input
                  id="storeNumber"
                  type="text"
                  value={formData.storeNumber}
                  onChange={(e) => handleInputChange('storeNumber', e.target.value)}
                  placeholder="Enter store number"
                  disabled={isLoading}
                />
              </FormGroup>

            </FormGrid>

            {/* Location */}
            <SectionTitle>
              <MapPin />
              Location
            </SectionTitle>
            
            <FormGrid>
              <FormGroup style={{ gridColumn: '1 / -1' }}>
                <Label htmlFor="address">
                  Address
                </Label>
                <Input
                  id="address"
                  type="text"
                  value={formData.address}
                  onChange={(e) => handleInputChange('address', e.target.value)}
                  placeholder="Enter street address"
                  disabled={isLoading}
                />
              </FormGroup>

              <FormGroup>
                <Label htmlFor="city">
                  City
                </Label>
                <Input
                  id="city"
                  type="text"
                  value={formData.city}
                  onChange={(e) => handleInputChange('city', e.target.value)}
                  placeholder="Enter city"
                  disabled={isLoading}
                />
              </FormGroup>

              <FormGroup>
                <Label htmlFor="state">
                  State
                </Label>
                <Input
                  id="state"
                  type="text"
                  value={formData.state}
                  onChange={(e) => handleInputChange('state', e.target.value)}
                  placeholder="Enter state"
                  disabled={isLoading}
                />
              </FormGroup>

              <FormGroup>
                <Label htmlFor="zipCode">
                  ZIP Code
                </Label>
                <Input
                  id="zipCode"
                  type="text"
                  value={formData.zipCode}
                  onChange={(e) => handleInputChange('zipCode', e.target.value)}
                  placeholder="Enter ZIP code"
                  disabled={isLoading}
                />
              </FormGroup>
            </FormGrid>

            {/* Contact Information */}
            <SectionTitle>
              <Phone />
              Contact Information
            </SectionTitle>
            
            <FormGrid>
              <FormGroup>
                <Label htmlFor="telephone">
                  Phone Number
                </Label>
                <Input
                  id="telephone"
                  type="tel"
                  value={formData.telephone}
                  onChange={(e) => handleInputChange('telephone', e.target.value)}
                  placeholder="Enter phone number"
                  disabled={isLoading}
                />
                {errors.telephone && <ErrorMessage>{errors.telephone}</ErrorMessage>}
              </FormGroup>

              <FormGroup>
                <Label htmlFor="email">
                  Email Address
                </Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleInputChange('email', e.target.value)}
                  placeholder="Enter email address"
                  disabled={isLoading}
                />
                {errors.email && <ErrorMessage>{errors.email}</ErrorMessage>}
              </FormGroup>
            </FormGrid>

            {/* Assignment */}
            <SectionTitle>
              <User />
              Assignment
            </SectionTitle>
            
            <FormGrid>
              <FormGroup style={{ gridColumn: '1 / -1' }}>
                <Label>
                  Assigned Sales Representatives
                  {((store as any).SalesGroup || store?.salesGroup) && (
                    <span style={{ 
                      marginLeft: Spacing.xs, 
                      fontSize: Typography.fontSize.xs,
                      fontWeight: Typography.fontWeight.normal,
                      color: ThemeColors.text.secondary
                    }}>
                      (Auto-matched by SalesGroup: {(store as any).SalesGroup || store.salesGroup})
                    </span>
                  )}
                </Label>
                <div style={{
                  maxHeight: '200px',
                  overflowY: 'auto',
                  border: `1px solid ${ThemeColors.border.main}`,
                  borderRadius: BorderRadius.sm,
                  padding: Spacing.sm,
                  backgroundColor: 'white'
                }}>
                  {salesReps.map(rep => {
                    const storeSalesGroup = (((store as any).SalesGroup || store?.salesGroup) || '').toUpperCase();
                    const repSalesGroup = (rep.salesGroup || '').toUpperCase();
                    const isMatchedBySalesGroup = storeSalesGroup && repSalesGroup === storeSalesGroup;
                    const isChecked = assignedUserIds.includes(rep.id);
                    
                    return (
                      <label key={rep.id} style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: Spacing.sm,
                        padding: `${Spacing.xs} 0`,
                        cursor: 'pointer'
                      }}>
                        <input
                          type="checkbox"
                          checked={isChecked}
                          onChange={() => handleSalesRepToggle(rep.id)}
                          disabled={isLoading}
                          style={{
                            width: '16px',
                            height: '16px',
                            accentColor: ThemeColors.primary.main
                          }}
                        />
                        <span style={{ 
                          fontSize: Typography.fontSize.sm,
                          color: isMatchedBySalesGroup ? ThemeColors.primary.main : ThemeColors.text.primary,
                          fontWeight: isMatchedBySalesGroup ? Typography.fontWeight.semibold : Typography.fontWeight.normal
                        }}>
                          {rep.name} {rep.username && `(${rep.username})`}
                          {isMatchedBySalesGroup && (
                            <span style={{
                              marginLeft: Spacing.xs,
                              fontSize: Typography.fontSize.xs,
                              color: ThemeColors.success.main
                            }}>
                              (Matches SalesGroup: {rep.salesGroup})
                            </span>
                          )}
                        </span>
                      </label>
                    );
                  })}
                  {salesReps.length === 0 && (
                    <div style={{ 
                      padding: Spacing.md, 
                      textAlign: 'center', 
                      color: ThemeColors.text.secondary,
                      fontSize: Typography.fontSize.sm
                    }}>
                      No sales representatives available
                    </div>
                  )}
                </div>
                <div style={{ 
                  marginTop: Spacing.xs, 
                  fontSize: Typography.fontSize.xs, 
                  color: ThemeColors.text.secondary 
                }}>
                  {assignedUserIds.length} sales rep{assignedUserIds.length !== 1 ? 's' : ''} assigned
                  {((store as any).SalesGroup || store?.salesGroup) && salesReps.some(r => (r.salesGroup || '').toUpperCase() === (((store as any).SalesGroup || store.salesGroup) || '').toUpperCase()) && (
                    <span style={{ marginLeft: Spacing.sm, color: ThemeColors.primary.main }}>
                      ({salesReps.filter(r => (r.salesGroup || '').toUpperCase() === (((store as any).SalesGroup || store.salesGroup) || '').toUpperCase()).length} matched by SalesGroup)
                    </span>
                  )}
                </div>
              </FormGroup>

              <FormGroup>
                <Label htmlFor="status">
                  Status
                </Label>
                <Select
                  id="status"
                  value={formData.status}
                  onChange={(e) => handleInputChange('status', e.target.value)}
                  disabled={isLoading}
                >
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                  <option value="pending">Pending</option>
                </Select>
              </FormGroup>
            </FormGrid>

            {/* Notes */}
            <SectionTitle>
              <FileText />
              Additional Notes
            </SectionTitle>
            
            <FormGroup>
              <Label htmlFor="notes">
                Notes
              </Label>
              <TextArea
                id="notes"
                value={formData.notes}
                onChange={(e) => handleInputChange('notes', e.target.value)}
                placeholder="Enter any additional notes about this store..."
                disabled={isLoading}
              />
            </FormGroup>
          </FormContent>

          <FormActions>
            <Button
              type="button"
              variant="secondary"
              onClick={onCancel}
              disabled={isLoading}
            >
              <X />
              Cancel
            </Button>
            <Button
              type="submit"
              variant="primary"
              disabled={isLoading}
            >
              <Save />
              {mode === 'create' ? 'Create Store' : 'Save Changes'}
            </Button>
          </FormActions>
        </form>
      </FormContainer>
    </FormOverlay>
  );
};

